/**
 * SSOT for agent hotel booking → dashboard success navigation.
 * Canonical success id is hotelpackages.id; legacy orders.id remains supported via idSource.
 */
import { traceHotelEngineBookingContext } from "../../features/hotelSearchEngine/hotelSearchEngine";
export const BOOKING_SUCCESS_TYPES = {
  HOTEL: "hotel",
  LDS: "lds",
  DYNAMIC_PACKAGE: "dynamic_package",
  ORGANIZED_TOUR: "organized_tour",
};

export const HOTEL_SUCCESS_ID_SOURCES = {
  HOTELPACKAGE: "hotelpackages.id",
  ORDERS: "orders.id",
  HR_ORDER_NUMBER: "hotelpackages.hr_order_number",
};

/** Explicit payment intents carried on success-page navigation state. */
export const HOTEL_SUCCESS_PAYMENT_INTENTS = {
  PAY_LATER: "pay_later",
  PAY_NOW: "pay_now",
  CONTINUE_PAYMENT: "continue_payment",
};

export const HOTEL_SUCCESS_BOOKING_INTENTS = {
  BOOK_NOW_PAY_LATER: "book_now_pay_later",
};

function normalizeHotelSuccessIntent(value) {
  if (value == null || value === "") return null;
  return String(value).trim().toLowerCase().replace(/-/g, "_");
}

function readNavigationState(navigationState, location) {
  if (navigationState && typeof navigationState === "object") return navigationState;
  if (location?.state && typeof location.state === "object") return location.state;
  return {};
}

function normalizeBookingIds(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "{}" || trimmed === "[]") return [];
    return trimmed
      .replace(/[{}[\]"']/g, "")
      .split(/[,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value != null ? [String(value).trim()].filter(Boolean) : [];
}

function hasValidSupplierBookingId(value) {
  return normalizeBookingIds(value).some(
    (item) => item && item !== "-1" && item.toLowerCase() !== "null"
  );
}

function isHotelSupplierConfirmedOrder(orderData) {
  if (!orderData || typeof orderData !== "object") return false;
  const completionState = String(orderData.completion_state || "").trim();
  const postBookingState = String(orderData.post_booking_state || "").trim();
  const supplierStatus = String(
    orderData.supplier_booking_status || orderData.orderstatus || ""
  )
    .trim()
    .toUpperCase();
  const supplierOrderId = orderData.supplier_order_id ?? orderData.orderid;
  return (
    orderData.booked === true ||
    orderData.supplier_confirmed === true ||
    hasValidSupplierBookingId(orderData.bookingid) ||
    hasValidSupplierBookingId(orderData.supplier_booking_id) ||
    (supplierOrderId != null &&
      String(supplierOrderId).trim() !== "" &&
      String(supplierOrderId).trim() !== "-1") ||
    completionState === "SUPPLIER_CONFIRMED_LOCAL_FINALIZE_FAILED" ||
    postBookingState === "SUPPLIER_CONFIRMED_LOCAL_FINALIZE_FAILED" ||
    ["OK", "CONFIRMED", "RESERVED", "BOOKED"].includes(supplierStatus)
  );
}

/**
 * Resolve payment/booking intent from navigation state (location.state SSOT).
 */
export function resolveHotelSuccessPaymentIntent(navigationState, location) {
  const state = readNavigationState(navigationState, location);
  return (
    normalizeHotelSuccessIntent(state.paymentIntent) ??
    normalizeHotelSuccessIntent(state.bookingIntent)
  );
}

export function isHotelPayLaterPaymentIntent(intent) {
  const normalized = normalizeHotelSuccessIntent(intent);
  return (
    normalized === HOTEL_SUCCESS_PAYMENT_INTENTS.PAY_LATER ||
    normalized === HOTEL_SUCCESS_BOOKING_INTENTS.BOOK_NOW_PAY_LATER
  );
}

export function isHotelPayNowPaymentIntent(intent) {
  const normalized = normalizeHotelSuccessIntent(intent);
  return (
    normalized === HOTEL_SUCCESS_PAYMENT_INTENTS.PAY_NOW ||
    normalized === HOTEL_SUCCESS_PAYMENT_INTENTS.CONTINUE_PAYMENT
  );
}

/**
 * Order row signals pay-later success (booked reservation awaiting payment).
 */
export function isHotelPayLaterOrderState(orderData) {
  if (!orderData || typeof orderData !== "object") return false;

  const status = normalizeHotelSuccessIntent(orderData.status);
  const paymentStatus = normalizeHotelSuccessIntent(
    orderData.payment_status ?? orderData.paymentStatus
  );

  if (
    status === "waiting_for_payment" ||
    paymentStatus === "waiting_for_payment"
  ) {
    return true;
  }

  if (orderData.booked === true && orderData.paid !== true) {
    return true;
  }

  return false;
}

/**
 * Booking did not succeed — stay on success/error UX; never auto-redirect to payment.
 */
export function isHotelSuccessBookingFailure(orderData) {
  if (!orderData || typeof orderData !== "object") return true;

  const status = String(orderData.status || "").trim().toLowerCase();
  if (status === "fail" || status === "perms") return true;

  if (isHotelSupplierConfirmedOrder(orderData)) return false;
  if (orderData.booked === true) return false;

  const hasOrderId =
    orderData.id != null ||
    orderData.orderid != null ||
    orderData.internal_order_id != null ||
    orderData.hotelpackage_id != null;

  if (
    hasOrderId &&
    (status === "ok" || status === "waiting_for_payment" || status === "")
  ) {
    return false;
  }

  return true;
}

/**
 * Whether success page should redirect to PaymentAgent.
 * Never redirects solely because orderData.errors.length > 0.
 */
export function shouldRedirectHotelSuccessToPayment(
  orderData,
  navigationState,
  location
) {
  const intent = resolveHotelSuccessPaymentIntent(navigationState, location);

  if (isHotelPayLaterPaymentIntent(intent)) {
    return false;
  }

  if (isHotelPayNowPaymentIntent(intent)) {
    return !isHotelSuccessBookingFailure(orderData);
  }

  if (isHotelPayLaterOrderState(orderData)) {
    return false;
  }

  if (isHotelSuccessBookingFailure(orderData)) {
    return false;
  }

  return false;
}

/**
 * Agent checkout for a saved hotel order: installments and Yaad Sarig live on PaymentAgent.
 * The id is orders.id, which is the row the payment screen loads.
 */
export function buildHotelOrderPaymentPath(persist) {
  const data = persist && typeof persist === "object" ? persist : {};
  const ordersId = data.order_id ?? data.internal_order_id ?? data.ordersId ?? null;
  if (ordersId == null || String(ordersId).trim() === "") return null;
  return `/p/dashboard/payment/${encodeURIComponent(String(ordersId).trim())}?idSource=orders.id&from=orders`;
}

/**
 * Build payment redirect target from success context (id + idSource SSOT).
 */
export function buildHotelSuccessPaymentRedirectTarget(
  currentOrderId,
  orderData,
  navigationState,
  location
) {
  const bookingContext = resolveHotelSuccessPageBookingContext(
    currentOrderId,
    readNavigationState(navigationState, location)
  );

  const idSource =
    bookingContext.idSource ??
    (orderData?.hotelpackage_id != null || orderData?.hotel_package_id != null
      ? HOTEL_SUCCESS_ID_SOURCES.HOTELPACKAGE
      : HOTEL_SUCCESS_ID_SOURCES.ORDERS);

  let paymentRouteId = currentOrderId;

  if (idSource === HOTEL_SUCCESS_ID_SOURCES.HOTELPACKAGE) {
    paymentRouteId =
      bookingContext.successId ??
      orderData?.hotelpackage_id ??
      orderData?.hotel_package_id ??
      currentOrderId;
  } else if (idSource === HOTEL_SUCCESS_ID_SOURCES.ORDERS) {
    paymentRouteId =
      bookingContext.ordersId ??
      orderData?.internal_order_id ??
      orderData?.order_row_id ??
      orderData?.id ??
      currentOrderId;
  } else {
    paymentRouteId = bookingContext.successId ?? currentOrderId;
  }

  const encodedId = encodeURIComponent(String(paymentRouteId));
  const encodedSource = encodeURIComponent(idSource);

  return {
    path: `/p/dashboard/payment/${encodedId}?idSource=${encodedSource}`,
    state: { idSource },
  };
}

/**
 * @param {object|null|undefined} responseData bookHotel / composite response body
 */
export function resolveHotelSuccessNavigationFromBookingResponse(responseData) {
  const data = responseData && typeof responseData === "object" ? responseData : {};
  const hotelPackageId =
    data.hotelpackage_id ??
    data.hotel_package_id ??
    data.internal_hotelpackage_id ??
    null;
  const ordersId =
    data.internal_order_id ??
    data.order_id ??
    data.id ??
    data.orderID ??
    null;
  const hrOrderNumber = data.hr_order_number ?? data.hrOrderNumber ?? null;

  if (hotelPackageId != null && String(hotelPackageId).trim() !== "") {
    const nav = {
      successId: String(hotelPackageId).trim(),
      bookingType: BOOKING_SUCCESS_TYPES.HOTEL,
      idSource: HOTEL_SUCCESS_ID_SOURCES.HOTELPACKAGE,
      hrOrderNumber: hrOrderNumber ? String(hrOrderNumber).trim() : null,
      ordersId: ordersId != null ? String(ordersId).trim() : null,
    };
    traceHotelEngineBookingContext({ stage: "success_navigation", ...nav, responseHotelPackageId: hotelPackageId });
    return nav;
  }

  if (hrOrderNumber && /^HR-\d{8}$/i.test(String(hrOrderNumber).trim())) {
    const nav = {
      successId: String(hrOrderNumber).trim().toUpperCase(),
      bookingType: BOOKING_SUCCESS_TYPES.HOTEL,
      idSource: HOTEL_SUCCESS_ID_SOURCES.HR_ORDER_NUMBER,
      hrOrderNumber: String(hrOrderNumber).trim().toUpperCase(),
      ordersId: ordersId != null ? String(ordersId).trim() : null,
    };
    traceHotelEngineBookingContext({ stage: "success_navigation", ...nav });
    return nav;
  }

  if (ordersId != null && String(ordersId).trim() !== "") {
    const nav = {
      successId: String(ordersId).trim(),
      bookingType: BOOKING_SUCCESS_TYPES.HOTEL,
      idSource: HOTEL_SUCCESS_ID_SOURCES.ORDERS,
      hrOrderNumber: null,
      ordersId: String(ordersId).trim(),
    };
    traceHotelEngineBookingContext({ stage: "success_navigation", ...nav });
    return nav;
  }

  const nav = {
    successId: null,
    bookingType: BOOKING_SUCCESS_TYPES.HOTEL,
    idSource: null,
    hrOrderNumber: null,
    ordersId: null,
  };
  traceHotelEngineBookingContext({ stage: "success_navigation_unresolved", responseData: data });
  return nav;
}

/**
 * @param {object} nav from resolveHotelSuccessNavigationFromBookingResponse
 * @param {{ paymentIntent?: string, bookingIntent?: string }} [options]
 */
export function buildHotelSuccessNavigateState(nav, options = {}) {
  const { paymentIntent, bookingIntent } = options;
  return {
    bustCache: true,
    bookedAt: Date.now(),
    bookingType: nav?.bookingType ?? BOOKING_SUCCESS_TYPES.HOTEL,
    idSource: nav?.idSource ?? null,
    hrOrderNumber: nav?.hrOrderNumber ?? null,
    ordersId: nav?.ordersId ?? null,
    ...(paymentIntent ? { paymentIntent } : {}),
    ...(bookingIntent ? { bookingIntent } : {}),
  };
}

const UUID_SUCCESS_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Resolve booking context for success page reload (route params + location.state).
 * UUID route ids are LDS (bank-transfer / package) — never default them to hotel.
 */
export function resolveHotelSuccessPageBookingContext(currentOrderId, locationState) {
  const state = locationState && typeof locationState === "object" ? locationState : {};
  const rawId = currentOrderId == null ? "" : String(currentOrderId).trim();
  const isHrRef = /^HR-\d{8}$/i.test(rawId);
  const isUuid = UUID_SUCCESS_ID_RE.test(rawId);
  const defaultBookingType = isUuid
    ? BOOKING_SUCCESS_TYPES.LDS
    : BOOKING_SUCCESS_TYPES.HOTEL;

  return {
    successId: isHrRef ? rawId.toUpperCase() : rawId,
    bookingType: state.bookingType ?? defaultBookingType,
    idSource:
      state.idSource ??
      (isHrRef ? HOTEL_SUCCESS_ID_SOURCES.HR_ORDER_NUMBER : null),
    hrOrderNumber: state.hrOrderNumber ?? (isHrRef ? rawId.toUpperCase() : null),
    ordersId: state.ordersId ?? null,
  };
}

export function buildHotelSuccessOrderRequestPayload(context, agentId, { isAdmin = false } = {}) {
  const ctx = context || {};
  const rawId = ctx.successId;
  const parsedId = /^HR-\d{8}$/i.test(String(rawId || ""))
    ? String(rawId).trim().toUpperCase()
    : Number.isFinite(Number(rawId))
      ? parseInt(String(rawId), 10)
      : rawId;
  const idAsString = parsedId == null ? "" : String(parsedId).trim();
  const defaultBookingType = UUID_SUCCESS_ID_RE.test(idAsString)
    ? BOOKING_SUCCESS_TYPES.LDS
    : BOOKING_SUCCESS_TYPES.HOTEL;

  return {
    id: parsedId,
    agentID: agentId,
    bookingType: ctx.bookingType ?? defaultBookingType,
    idSource: ctx.idSource ?? null,
    isAdmin: Boolean(isAdmin),
  };
}

export function logBookingSuccessRouteTrace(payload) {
  if (!import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[BOOKING_SUCCESS_ROUTE_TRACE]", payload);
  } catch {
    /* ignore */
  }
}
